<template>
  <ModalBase
    :show-modal="true"
    @close-modal="close"
    :auto-h="true"
    :narrow="true"
    :overlay-scrollb="false"
    class="add-supplier-modal"
  >
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal title-modal--simple">
        <template v-slot:title>
          <span
            >{{ $gettext('We found') }}&nbsp;{{ supplierList.length }}&nbsp;{{
              supplierList.length === 1 ? $gettext('new supplier') : $gettext('new suppliers')
            }}</span
          >
        </template>
        <template v-slot:subtitle>
          <span class="subtitle-text">{{ $gettext('Do you want to add this supplier to your contact list?') }}</span>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <div ref="stepContainer" class="supplier-step-container">
        <transition
          name="supplier-step"
          mode="out-in"
          @before-enter="beforeStepEnter"
          @before-leave="beforeStepLeave"
          @enter="onStepEnter"
          @after-leave="afterStepLeave"
          @after-enter="afterStepEnter"
        >
          <div :key="supplierFormKey" class="supplier-step-panel">
            <NewSupplierForm ref="newSupplierForm" v-model="newSupplierData" />
          </div>
        </transition>
      </div>
    </template>
    <template v-slot:actions>
      <div class="supplier-actions-row">
        <div class="supplier-actions-counter">
          <span class="supplier-counter-badge"> {{ displaySupplierIndex }} / {{ supplierList.length }} </span>
        </div>
        <div class="supplier-actions-buttons">
          <GekkoButton @click="skipOrFinish()" icon="fa-solid" :text="buttonSkipText" variant="link" class="ml-auto" />
          <GekkoButton @click="saveAndGetNext()" icon="fa-solid fa-save" :text="$gettext('Save')" class="ml-auto" />
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import ModalBase from '@/components/base_components/ModalBase'
import GekkoButton from '@/components/GekkoButton'
import TitleBar from '@/components/TitleBar'
import NewSupplierForm from '@/components/costs/ui/NewSupplierForm.vue'
import CustomerService from '@/store/services/customer.service'
import CostService from '@/store/services/cost.service'
import { mapGetters } from 'vuex'

export default {
  name: 'AddSupplierListModal',
  components: {
    ModalBase,
    TitleBar,
    GekkoButton,
    NewSupplierForm,
  },
  props: {
    supplierList: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    if (this.hasSupplierAtIndex) {
      this.newSupplierData = { ...(this.supplierList[this.savingIndex].new_supplier || {}) }
      return
    }

    this.newSupplierData = null
  },
  data() {
    return {
      newSupplierData: null,
      savingIndex: 0,
      stepResizeObserver: null,
      resizeRafId: null,
    }
  },
  mounted() {
    this.$nextTick(() => {
      const panel = this.$el.querySelector('.supplier-step-panel')
      if (panel) {
        this.observeStepPanel(panel)
        this.setStepContainerHeight(panel.offsetHeight)
      }
    })
  },
  beforeDestroy() {
    this.disconnectStepObserver()
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId)
      this.resizeRafId = null
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
    }),
    hasSupplierAtIndex() {
      return (
        this.savingIndex >= 0 && this.savingIndex < this.supplierList.length && this.savingIndex in this.supplierList
      )
    },
    supplierFormKey() {
      const currentSupplier = this.supplierList[this.savingIndex]
      return currentSupplier?.cost_id || `supplier-form-${this.savingIndex}`
    },
    displaySupplierIndex() {
      if (!this.supplierList.length) {
        return 0
      }

      return Math.min(this.savingIndex + 1, this.supplierList.length)
    },
    buttonSkipText() {
      if (this.savingIndex >= this.supplierList.length - 1) {
        return this.$gettext('Done')
      }
      return this.$gettext('Skip')
    },
  },
  methods: {
    setStepContainerHeight(height) {
      const container = this.$refs.stepContainer
      if (!container || height == null) return
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId)
      }
      this.resizeRafId = requestAnimationFrame(() => {
        container.style.height = `${Math.ceil(height)}px`
      })
    },
    disconnectStepObserver() {
      if (this.stepResizeObserver) {
        this.stepResizeObserver.disconnect()
        this.stepResizeObserver = null
      }
    },
    observeStepPanel(panelEl) {
      this.disconnectStepObserver()
      if (typeof ResizeObserver === 'undefined' || !panelEl) return
      this.stepResizeObserver = new ResizeObserver((entries) => {
        if (!entries || !entries.length) return
        this.setStepContainerHeight(entries[0].contentRect.height)
      })
      this.stepResizeObserver.observe(panelEl)
    },
    beforeStepEnter() {
      const container = this.$refs.stepContainer
      if (!container) return
      if (!container.style.height || container.style.height === 'auto') {
        container.style.height = `${container.offsetHeight}px`
      }
    },
    beforeStepLeave(el) {
      const container = this.$refs.stepContainer
      if (!container || !el) return

      container.classList.add('is-transitioning')

      const rect = el.getBoundingClientRect()
      container.style.height = `${rect.height}px`
      this.disconnectStepObserver()

      el.style.position = 'absolute'
      el.style.left = '0'
      el.style.top = '0'
      el.style.width = `${rect.width}px`
    },
    onStepEnter(el) {
      const container = this.$refs.stepContainer
      if (!container) return

      this.observeStepPanel(el)
      this.$nextTick(() => {
        this.setStepContainerHeight(el.offsetHeight)
      })
    },
    afterStepLeave(el) {
      if (!el) return
      el.style.position = ''
      el.style.left = ''
      el.style.top = ''
      el.style.width = ''
    },
    afterStepEnter() {
      const container = this.$refs.stepContainer
      if (container) container.classList.remove('is-transitioning')

      const panel = this.$el.querySelector('.supplier-step-panel')
      if (panel) this.setStepContainerHeight(panel.offsetHeight)
    },
    close() {
      this.$emit('close')
    },
    saveAndGetNext() {
      this.newSupplierData.is_supplier = true
      this.newSupplierData.is_customer = false
      this.$refs.newSupplierForm.assignTaxIdFields()
      this.newSupplierData.owner_company = this.getCompanyId

      CustomerService.create(this.newSupplierData)
        .then((response) => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Contact added'),
            text: this.$gettext('The supplier was added to your contact list.'),
            variant: 'success',
            duration: 4,
          })

          const cost = {
            number: this.supplierList[this.savingIndex].cost_number,
            owner_company: this.supplierList[this.savingIndex].cost_owner_company,
            is_automatically_scanned: true,
            supplier: response.data.id,
          }

          CostService.update(this.supplierList[this.savingIndex].cost_id, cost)
            .then(() => {
              this.$emit('created')

              this.savingIndex += 1
              if (!this.hasSupplierAtIndex) {
                this.close()
                return
              }

              this.newSupplierData = { ...(this.supplierList[this.savingIndex].new_supplier || {}) }
            })
            .catch(() => {
              this.$store.commit('setAlert', {
                title: this.$gettext('Error adding contact'),
                text: this.$gettext(
                  'The contact was added but we could not link it to the cost. Please check your contact list and link it manually.'
                ),
                variant: 'danger',
                duration: 4,
              })
            })
        })
        .catch((error) => {
          let msg = this.$gettext('The contact could not be added to your contact list.')
          if (error.name) {
            msg = this.$gettext('Contact name already exists')
          }

          this.$store.commit('setAlert', {
            title: this.$gettext('Error adding contact'),
            text: msg,
            variant: 'danger',
            duration: 4,
          })
        })
    },
    skipOrFinish() {
      if (!this.hasSupplierAtIndex) {
        this.close()
        return
      }

      this.savingIndex += 1
      if (!this.hasSupplierAtIndex) {
        this.close()
        return
      }

      this.newSupplierData = { ...(this.supplierList[this.savingIndex].new_supplier || {}) }
    },
  },
}
</script>

<style lang="scss" scoped>

.subtitle-text {
  font-size: 16px;
  color: $colorTextLight;
}

.supplier-step-container {
  position: relative;
  overflow: hidden;
}

.supplier-step-container.is-transitioning {
  transition: height 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.supplier-step-panel {
  width: 100%;
}

.supplier-step-enter-active,
.supplier-step-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.supplier-step-enter,
.supplier-step-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.supplier-counter-badge {
  color: $colorUnassigned;
  font-size: 14px;
}

.supplier-actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.supplier-actions-counter {
  display: flex;
  align-items: center;
}

.supplier-actions-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-left: auto;
}
</style>